import React from 'react';

const URL_PATTERN =
  /(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|]/gi;

/**
 * Parse text and turn URLs into anchor tags.
 *
 * Implementation note: this used to render via `dangerouslySetInnerHTML` +
 * DOMPurify, but that destroys the inner DOM whenever the parent re-renders
 * (any change to the props object triggers a full innerHTML reset), which
 * killed live text selections inside notes (ATT-1458 — drag flickered and
 * mouseup yielded no selection). Rendering as real React nodes keeps the
 * text DOM stable across re-renders, so native selection survives.
 */
export default function parseUrlsAndCreateHTMLandAnchors(
  text: string
): React.ReactNode {
  if (!text) return text;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  URL_PATTERN.lastIndex = 0;
  while ((match = URL_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    parts.push(
      <a
        key={`url-${match.index}`}
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        className='link-primary link'
      >
        {url}
      </a>
    );
    lastIndex = match.index + url.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  if (parts.length === 0) return text;
  if (parts.length === 1 && typeof parts[0] === 'string') return parts[0];
  return <>{parts}</>;
}
